/**
 * Schritt „Ablauf“ (Reisegruppen-Widget T5, docs/PLAN-REISEGRUPPEN-WIDGET.md A2, Mockup entwurf-6.html
 * Reiter „MAESTRO-Einstellungen“): Kachel-Ablauf an/aus, Sprache, Gruppengrößen, Monate/Später/Offen,
 * feste Werte, Menüs mit optionaler Katalog-Verknüpfung, Dankeseiten-Fragen und die Zeilen „auf einen Blick“.
 *
 * Darüber steht die Beratung: je Entscheidung eine Empfehlung mit Begründung; wer abweicht, bekommt
 * einen Hinweis, nie eine Sperre (Ausnahme: rechtliche Grenze, siehe beratung.ts).
 *
 * Preis-Integrität: Menüs werden nur mit einem Katalog-Paket VERKNÜPFT. Es gibt hier kein Preisfeld;
 * der Preis steht im Katalog und wird serverseitig gelesen.
 */
import { Button, Card, Checkbox, Icon, Input, Select, Toggle } from "../../../ui/components";
import { useApi } from "../../../hooks/useApi";
import type { WidgetEditor } from "../useWidgetEditorForm";
import {
  ABLAUF_SPRACHEN,
  ABLAUF_SPRACHE_LABEL,
  ANTWORTZUSAGE_VORSCHLAG,
  EDITOR_NACHFRAGE_FELDER,
  MAX_ANTWORTZUSAGE_LAENGE,
  MAX_BLICK_LAENGE,
  MAX_BLICK_ZEILEN,
  MAX_HINWEIS_UEBER_LAENGE,
  MAX_FRIST_TAGE,
  MAX_MENUES,
  MAX_TELEFON_LAENGE,
  MAX_TELEFON_SPRACHEN_LAENGE,
  MAX_URL_LAENGE,
  bereichLabels,
  neuerMenueSchluessel,
  parseGaesteBereiche,
  pruefeGaesteText,
  type AblaufSprache,
} from "../ablauf-modell";
import { aenderungFuer, auswertung, beraten, type Entscheidung } from "../beratung";

const TITEL = "font-body-sm text-body-sm font-semibold text-on-surface dark:text-inverse-on-surface";
const HILFE = "font-label-md text-label-md text-on-surface-variant";

/** Eine Zeile des Katalogs (GET /api/packages) — nur, was die Verknüpfung braucht. */
interface KatalogPaket {
  id: string;
  name: string;
  kind: string;
  archivedAt?: string | null;
  /** Gaeste-Grenzen des Pakets; leer/0 = offen. */
  minGuests?: number | null;
  maxGuests?: number | null;
}

/** „erscheint für Gruppen von X bis Y Personen“ (bzw. ab X / bis Y) oder null ohne Grenzen. */
export function gruppenHinweis(min?: number | null, max?: number | null): string | null {
  const von = typeof min === "number" && min > 0 ? min : null;
  const bis = typeof max === "number" && max > 0 ? max : null;
  if (von !== null && bis !== null) return `erscheint für Gruppen von ${von} bis ${bis} Personen`;
  if (von !== null) return `erscheint für Gruppen ab ${von} Personen`;
  if (bis !== null) return `erscheint für Gruppen bis ${bis} Personen`;
  return null;
}

const KEIN_PAKET = "__kein__";

export function EntscheidungsKarte({ e, onWahl }: { e: Entscheidung; onWahl?: (index: number) => void }) {
  return (
    <div className="rounded-xl border border-outline-variant/30 p-3 dark:border-white/10" data-testid={`beratung-${e.key}`} data-abweichung={e.abweichung}>
      <div className="flex flex-wrap items-center gap-2">
        <p className={TITEL}>{e.titel}</p>
        <span
          className={`rounded-full px-2 py-0.5 font-label-md text-label-md font-semibold ${
            e.abweichung ? "bg-tertiary-container/60 text-on-tertiary-container" : "bg-[#F0EAE0] text-secondary dark:bg-white/[0.08] dark:text-[#b8ae9e]"
          }`}
        >
          {e.abweichung ? "Abweichung" : "Empfohlen"}
        </span>
      </div>
      <div role="group" aria-label={e.titel} className="mt-2 flex flex-wrap gap-2">
        {e.optionen.map((o, i) => {
          const aktiv = i === e.gewaehlt;
          const klasse = `rounded-full border px-3 py-1.5 text-left font-label-md text-label-md ${
            aktiv ? "border-primary bg-primary/10 font-semibold text-primary" : "border-outline-variant/40 text-on-surface-variant"
          }`;
          const text = (
            <>
              {o}
              {i === e.empfohlen && " · empfohlen"}
            </>
          );
          const aenderbar = onWahl && e.aendernIn === null;
          return aenderbar ? (
            <button key={o} type="button" aria-pressed={aktiv} className={klasse} onClick={() => onWahl(i)}>
              {text}
            </button>
          ) : (
            <span key={o} aria-current={aktiv ? "true" : undefined} className={klasse}>
              {text}
            </span>
          );
        })}
      </div>
      <p className={`mt-2 ${HILFE}`}>{e.begruendung}</p>
      {e.aendernIn && e.abweichung && <p className={`mt-1 ${HILFE}`}>Ändern unter: {e.aendernIn}.</p>}
      {e.hinweis && (
        <p role="status" className="mt-2 rounded-lg bg-tertiary-container/60 p-2 font-label-md text-label-md text-on-tertiary-container">
          <b>Das ist erlaubt.</b> {e.hinweis}
        </p>
      )}
      {e.sperre && (
        <p role="alert" className="mt-2 rounded-lg bg-error/10 p-2 font-label-md text-label-md text-error" data-testid={`sperre-${e.key}`}>
          <b>Rechtliche Grenze.</b> {e.sperre}
        </p>
      )}
    </div>
  );
}

/** Die Beratung als Karte: „x von y“ und je Entscheidung eine Empfehlung. Wiederverwendbar (Ablauf-Schritt). */
export function BeratungKarte({ editor }: { editor: WidgetEditor }) {
  const entscheidungen = beraten(editor.form);
  const stand = auswertung(entscheidungen);
  const gruppen = [...new Set(entscheidungen.map((e) => e.gruppe))];
  return (
    <Card className="space-y-3 p-4" data-testid="beratung-karte">
      <div className="flex items-baseline gap-3">
        <p className="text-[28px] font-bold leading-none text-primary" data-testid="beratung-stand">
          {stand.ok} von {stand.gesamt}
        </p>
        <p className={HILFE}>{stand.text}</p>
      </div>
      {gruppen.map((g) => (
        <div key={g} className="space-y-2">
          <p className="font-label-md text-label-md font-semibold uppercase tracking-wide text-on-surface-variant">{g}</p>
          {entscheidungen
            .filter((e) => e.gruppe === g)
            .map((e) => (
              <EntscheidungsKarte
                key={e.key}
                e={e}
                onWahl={(i) => {
                  const a = aenderungFuer(e.key, i);
                  if (a?.ablaufAn !== undefined) editor.ablaufAnAus(a.ablaufAn);
                }}
              />
            ))}
        </div>
      ))}
      <p className={HILFE}>
        <b>Immer an, keine Einstellung:</b> Tastaturbedienung, sichtbarer Fokus, Beschriftungen, Fehlermeldungen am Feld, Tippflächen ab 44 px,
        Bot-Abwehr ohne Cloudflare-Prüfung (unsichtbares Fangfeld, Mindestzeit, Mengenbegrenzung) und die Messung je Bildschirm auf dem Server,
        ohne Cookies und Kennungen.
      </p>
    </Card>
  );
}

export default function AblaufStep({ editor, onNext }: { editor: WidgetEditor; onNext: () => void }) {
  const { form } = editor;
  const a = form.ablauf;
  const katalog = useApi<KatalogPaket[]>(form.ablaufAn ? "/api/packages" : null);
  const menuePakete = (Array.isArray(katalog.data) ? katalog.data : []).filter((p) => p.kind === "menu" && !p.archivedAt);
  const gaesteFehler = pruefeGaesteText(a.gaesteText);
  const bereiche = bereichLabels(a.gaesteText);

  function dankeseiteUmschalten(key: string) {
    editor.ablaufAendern({ dankeseite: a.dankeseite.includes(key) ? a.dankeseite.filter((k) => k !== key) : [...a.dankeseite, key] });
  }

  function blickAendern(i: number, text: string) {
    editor.ablaufAendern({ blick: a.blick.map((z, n) => (n === i ? text : z)) });
  }

  return (
    <div className="space-y-4">
      <BeratungKarte editor={editor} />

      <Card className="space-y-4 p-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className={TITEL}>Kachel-Ablauf</p>
            <p className={`mt-1 ${HILFE}`}>
              Erst die Gruppengröße, dann der Monat, jeweils ein Tipp und sofort weiter; erst der letzte Bildschirm braucht die Tastatur.
              Aus: das Formular bleibt ein klassisches Formular. Ihre Einstellungen hier bleiben erhalten, wenn Sie ihn ausschalten.
            </p>
          </div>
          <Toggle checked={form.ablaufAn} onChange={editor.ablaufAnAus} label="Kachel-Ablauf" />
        </div>

        {form.ablaufAn && (
          <>
            <div className="grid gap-3 sm:grid-cols-2">
              <label className="block">
                <span className={`mb-1 block ${TITEL}`}>Sprache dieses Widgets</span>
                <Select
                  ariaLabel="Sprache dieses Widgets"
                  value={a.sprache}
                  onValueChange={(v) => editor.ablaufAendern({ sprache: v as AblaufSprache })}
                  options={ABLAUF_SPRACHEN.map((s) => ({ value: s, label: ABLAUF_SPRACHE_LABEL[s] }))}
                />
                <span className={`mt-1 block ${HILFE}`}>
                  Je Sprache ein eigenes Widget mit eigenen Texten und eigener Kennung, auch für die Bestätigungs-Mail. MAESTRO schlägt je Sprache
                  passende Texte vor, statt wörtlich zu übersetzen.
                </span>
              </label>
              <label className="block">
                <span className={`mb-1 block ${TITEL}`}>Monate zur Auswahl</span>
                <Select
                  ariaLabel="Monate zur Auswahl"
                  value={String(a.monate)}
                  onValueChange={(v) => editor.ablaufAendern({ monate: Number(v) })}
                  options={[3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((n) => ({ value: String(n), label: `${n} Monate` }))}
                />
                <span className={`mt-1 block ${HILFE}`}>Die nächsten Monate ab heute, immer mit vierstelligem Jahr.</span>
              </label>
            </div>

            <div>
              <label className="block">
                <span className={`mb-1 block ${TITEL}`}>Gruppengrößen (Untergrenzen)</span>
                <Input
                  aria-invalid={gaesteFehler.length > 0}
                  aria-describedby="ablauf-gaeste-hilfe"
                  value={a.gaesteText}
                  onChange={(e) => editor.ablaufAendern({ gaesteText: e.target.value })}
                  placeholder="10, 25, 40, 60, 80, 101"
                />
              </label>
              <p id="ablauf-gaeste-hilfe" className={`mt-1 ${HILFE}`}>
                Kommagetrennt, aufsteigend, höchstens 8. Die kleinste Zahl ist die Mindestgruppe. Die letzte heißt „über …“, bei 101 also „über 100“.
              </p>
              {gaesteFehler.length > 0 ? (
                <p role="alert" className="mt-1 font-label-md text-label-md text-error" data-testid="ablauf-gaeste-fehler">
                  {gaesteFehler[0]} Gespeichert wird erst, wenn das stimmt.
                </p>
              ) : (
                <p className={`mt-1 ${HILFE}`} data-testid="ablauf-gaeste-kacheln">
                  Kacheln: {bereiche.map((b) => b.text).join(" · ")}
                </p>
              )}
            </div>

            <div>
              <label className="block">
                <span className={`mb-1 block ${TITEL}`}>Kalender öffnet im</span>
                <Select
                  ariaLabel="Kalender öffnet im"
                  value={a.kalender?.monat ? String(a.kalender.monat) : "auto"}
                  onValueChange={(v) => editor.ablaufAendern({ kalender: v === "auto" ? {} : { monat: Number(v) } })}
                  options={[
                    { value: "auto", label: "Automatisch (je nach Anlass)" },
                    ...["Januar", "Februar", "März", "April", "Mai", "Juni", "Juli", "August", "September", "Oktober", "November", "Dezember"].map((n, i) => ({ value: String(i + 1), label: n })),
                  ]}
                />
                <span className={`mt-1 block ${HILFE}`}>
                  Der Gast wählt einen Tag im Kalender, optional einen zweiten als Ausweichtermin. Vergangene Tage und Schließtage (Einstellungen › Betrieb) sind gesperrt.
                </span>
              </label>
            </div>
          </>
        )}
      </Card>

      {form.ablaufAn && (
        <>
          <Card className="space-y-3 p-4">
            <p className={TITEL}>Feste Werte</p>
            <p className={HILFE}>
              Diese Angaben setzt MAESTRO bei jeder Anfrage selbst, der Gast wird nicht danach gefragt und kann sie nicht ändern.
            </p>
            <div className="grid gap-3 sm:grid-cols-2">
              <label className="block">
                <span className={`mb-1 block ${TITEL}`}>Anlass</span>
                <Input maxLength={80} value={a.eventType} onChange={(e) => editor.ablaufAendern({ eventType: e.target.value })} placeholder="Reisegruppe" />
              </label>
              <label className="block">
                <span className={`mb-1 block ${TITEL}`}>Art der Veranstaltung</span>
                <Select
                  ariaLabel="Art der Veranstaltung"
                  value={a.serviceKind}
                  onValueChange={(v) => editor.ablaufAendern({ serviceKind: v === "catering" ? "catering" : "event" })}
                  options={[
                    { value: "event", label: "Veranstaltung im Haus" },
                    { value: "catering", label: "Catering / außer Haus" },
                  ]}
                />
              </label>
            </div>
          </Card>

          <Card className="space-y-3 p-4">
            <div className="flex items-center justify-between gap-3">
              <p className={TITEL}>
                Menüs zur Auswahl ({a.menues.length}/{MAX_MENUES})
              </p>
              <button
                type="button"
                className="inline-flex items-center gap-1 font-label-md text-label-md text-primary hover:underline disabled:opacity-50"
                disabled={a.menues.length >= MAX_MENUES}
                onClick={() =>
                  editor.ablaufAendern({ menues: [...a.menues, { schluessel: neuerMenueSchluessel(a.menues), label: "", paketId: null }] })
                }
              >
                <Icon name="add" className="text-[16px]" /> Menü hinzufügen
              </button>
            </div>
            <p className={HILFE}>
              Ein Menü ist ein Wunsch, den der Gast auf der Seite per „Anfragen“ mitschickt. Verknüpfen Sie es mit einem Menü-Paket aus dem Katalog:
              dann zeigt das Widget dessen Preis. Einen Preis tippen Sie hier nie ein, er kommt immer aus dem Katalog. Ohne Verknüpfung erscheint
              keine Preiszeile.
            </p>
            {a.menues.length === 0 ? (
              <p className={HILFE}>Noch keine Menüs.</p>
            ) : (
              <ul className="space-y-3">
                {a.menues.map((m, i) => (
                  <li key={m.schluessel} className="rounded-xl bg-surface-container-lowest p-3 dark:bg-white/[0.03]">
                    <div className="flex flex-wrap items-end gap-2">
                      <label className="min-w-[180px] flex-1">
                        <span className={`mb-1 block ${HILFE}`}>Name des Menüs</span>
                        <Input
                          maxLength={80}
                          value={m.label}
                          placeholder="z. B. Menù Roma"
                          onChange={(e) =>
                            editor.ablaufAendern({ menues: a.menues.map((x, n) => (n === i ? { ...x, label: e.target.value } : x)) })
                          }
                        />
                      </label>
                      <label className="w-[240px] max-w-full">
                        <span className={`mb-1 block ${HILFE}`}>Paket im Katalog</span>
                        <Select
                          ariaLabel={`Katalog-Paket für Menü ${i + 1}`}
                          value={m.paketId ?? KEIN_PAKET}
                          onValueChange={(v) =>
                            editor.ablaufAendern({ menues: a.menues.map((x, n) => (n === i ? { ...x, paketId: v === KEIN_PAKET ? null : v } : x)) })
                          }
                          options={[
                            { value: KEIN_PAKET, label: "Nicht verknüpft (kein Preis)" },
                            ...menuePakete.map((p) => ({ value: p.id, label: p.name })),
                            ...(m.paketId && !menuePakete.some((p) => p.id === m.paketId) ? [{ value: m.paketId, label: "Verknüpftes Paket (nicht mehr im Katalog)" }] : []),
                          ]}
                        />
                      </label>
                      <button
                        type="button"
                        className="inline-flex items-center gap-1 pb-2 font-label-md text-label-md text-error hover:underline"
                        onClick={() => editor.ablaufAendern({ menues: a.menues.filter((_, n) => n !== i) })}
                      >
                        <Icon name="delete" className="text-[16px]" /> Entfernen
                      </button>
                    </div>
                    {(() => {
                      const paket = m.paketId ? menuePakete.find((p) => p.id === m.paketId) : undefined;
                      const hinweis = paket ? gruppenHinweis(paket.minGuests, paket.maxGuests) : null;
                      return hinweis ? <p className={`mt-2 ${HILFE}`} data-testid={`menue-gruppen-${m.schluessel}`}>{hinweis}</p> : null;
                    })()}
                  </li>
                ))}
              </ul>
            )}
            {a.menues.length > 0 && !katalog.loading && menuePakete.length === 0 && (
              <p className={HILFE}>Im Katalog gibt es noch keine Menü-Pakete. Legen Sie eines unter „Pakete“ an, dann lässt es sich hier verknüpfen.</p>
            )}
          </Card>

          <Card className="space-y-3 p-4">
            <p className={TITEL}>Auf einen Blick, über dem Formular</p>
            <p className={HILFE}>
              Informieren statt fragen: Genau danach vergleichen Veranstalter. Kurze Zeilen wie „Reiseleitung ab 25 Personen frei“. Schreiben Sie nur,
              was stimmt und was Sie belegen können.
            </p>
            <ul className="space-y-2">
              {a.blick.map((z, i) => (
                <li key={i} className="flex items-center gap-2">
                  <Input
                    aria-label={`Zeile ${i + 1}`}
                    maxLength={MAX_BLICK_LAENGE}
                    value={z}
                    onChange={(e) => blickAendern(i, e.target.value)}
                  />
                  <button
                    type="button"
                    aria-label={`Zeile ${i + 1} entfernen`}
                    className="text-error"
                    onClick={() => editor.ablaufAendern({ blick: a.blick.filter((_, n) => n !== i) })}
                  >
                    <Icon name="delete" className="text-[18px]" />
                  </button>
                </li>
              ))}
            </ul>
            <button
              type="button"
              className="inline-flex items-center gap-1 font-label-md text-label-md text-primary hover:underline disabled:opacity-50"
              disabled={a.blick.length >= MAX_BLICK_ZEILEN}
              onClick={() => editor.ablaufAendern({ blick: [...a.blick, ""] })}
            >
              <Icon name="add" className="text-[16px]" /> Zeile hinzufügen ({a.blick.length}/{MAX_BLICK_ZEILEN})
            </button>
            <label className="block">
              <span className={`mb-1 block ${TITEL}`}>Hinweis bei „über 100“</span>
              <Input maxLength={MAX_HINWEIS_UEBER_LAENGE} value={a.hinweisUeber} onChange={(e) => editor.ablaufAendern({ hinweisUeber: e.target.value })} />
            </label>
            <label className="block">
              <span className={`mb-1 block ${TITEL}`}>Link für Gruppen unter der Mindestgröße</span>
              <Input
                type="url"
                maxLength={MAX_URL_LAENGE}
                placeholder="https://ihre-website.de/reservieren"
                value={a.kleineGruppeUrl}
                onChange={(e) => editor.ablaufAendern({ kleineGruppeUrl: e.target.value })}
              />
              <span className={`mt-1 block ${HILFE}`}>
                Optional, muss mit https:// beginnen. Das Widget zeigt dann unter den Kacheln „Weniger als {parseGaesteBereiche(a.gaesteText)[0] ?? 10} Personen? Hier reservieren“ (mit Ihrer kleinsten Gruppengröße).
              </span>
            </label>
          </Card>

          <Card className="space-y-3 p-4" data-testid="kontakt-zusagen">
            <p className={TITEL}>Kontakt und Zusagen Ihres Betriebs</p>
            <p className={HILFE}>
              Diese Angaben stehen unter dem Knopf und auf der Dankeseite. Alles ist optional: Was Sie leer lassen, erscheint nicht, und MAESTRO
              erfindet nichts dazu.
            </p>
            <div className="grid gap-3 sm:grid-cols-2">
              <label className="block">
                <span className={`mb-1 block ${TITEL}`}>Telefonnummer</span>
                <Input maxLength={MAX_TELEFON_LAENGE} value={a.telefon} placeholder="+49 89 51519696" onChange={(e) => editor.ablaufAendern({ telefon: e.target.value })} />
                <span className={`mt-1 block ${HILFE}`}>So erscheint sie. Der Anruf-Link auf dem Handy entsteht daraus, dabei zählen nur Ziffern und das +.</span>
              </label>
              <label className="block">
                <span className={`mb-1 block ${TITEL}`}>Sprachen am Telefon</span>
                <Input
                  maxLength={MAX_TELEFON_SPRACHEN_LAENGE}
                  value={a.telefonSprachen}
                  placeholder="Deutsch, Italiano, English"
                  onChange={(e) => editor.ablaufAendern({ telefonSprachen: e.target.value })}
                />
                <span className={`mt-1 block ${HILFE}`}>Nur Sprachen, die dort auch wirklich jemand spricht. Erscheint hinter der Nummer.</span>
              </label>
              <label className="block">
                <span className={`mb-1 block ${TITEL}`}>Frist für die endgültige Personenzahl (Tage)</span>
                <Input
                  inputMode="numeric"
                  maxLength={2}
                  value={a.fristTage}
                  placeholder="7"
                  onChange={(e) => editor.ablaufAendern({ fristTage: e.target.value.replace(/\D/g, "") })}
                />
                <span className={`mt-1 block ${HILFE}`}>
                  Für den Satz „Finale Personenzahl und Allergien bis 7 Tage vorher“ (1 bis {MAX_FRIST_TAGE}). Leer: Der Satz entfällt.
                </span>
              </label>
              <label className="block">
                <span className={`mb-1 block ${TITEL}`}>Link zu den Menüs (PDF oder Seite)</span>
                <Input
                  type="url"
                  maxLength={MAX_URL_LAENGE}
                  placeholder="https://ihre-website.de/menues.pdf"
                  value={a.menueUrl}
                  onChange={(e) => editor.ablaufAendern({ menueUrl: e.target.value })}
                />
                <span className={`mt-1 block ${HILFE}`}>Erscheint auf der Dankeseite als „Speisekarte ansehen“. Muss mit https:// beginnen.</span>
              </label>
              <label className="block sm:col-span-2">
                <span className={`mb-1 block ${TITEL}`}>Link zur Anfahrt für Reisebusse</span>
                <Input
                  type="url"
                  maxLength={MAX_URL_LAENGE}
                  placeholder="https://ihre-website.de/anfahrt"
                  value={a.anfahrtUrl}
                  onChange={(e) => editor.ablaufAendern({ anfahrtUrl: e.target.value })}
                />
                <span className={`mt-1 block ${HILFE}`}>Wo Busse halten und parken dürfen. Muss mit https:// beginnen.</span>
              </label>
            </div>
            <div>
              <label className="block">
                <span className={`mb-1 block ${TITEL}`}>Antwortversprechen unter dem Knopf</span>
                <Input
                  maxLength={MAX_ANTWORTZUSAGE_LAENGE}
                  aria-describedby="ablauf-zusage-hilfe"
                  value={a.antwortzusage}
                  placeholder={ANTWORTZUSAGE_VORSCHLAG[a.sprache]}
                  onChange={(e) => editor.ablaufAendern({ antwortzusage: e.target.value })}
                />
              </label>
              <p id="ablauf-zusage-hilfe" className={`mt-1 ${HILFE}`} data-testid="antwortzusage-hinweis">
                <b>Versprechen Sie nur, was Sie halten.</b> Dieser Text steht anstelle einer gemessenen Zeit; eine Stundenzahl gibt MAESTRO nie vor.
                Empfohlen: „{ANTWORTZUSAGE_VORSCHLAG[a.sprache]}“. Eine kürzere Zusage erst, wenn Ihre echte Antwortzeit sie trägt (MAESTRO misst sie
                und zeigt sie erst ab 20 Anfragen in 90 Tagen). Leer: kein Versprechen; die gemessene Zeit erscheint dann nur, wenn sie vorliegt.
              </p>
              {a.antwortzusage.trim() !== ANTWORTZUSAGE_VORSCHLAG[a.sprache] && (
                <button
                  type="button"
                  className="mt-1 font-label-md text-label-md text-primary hover:underline"
                  onClick={() => editor.ablaufAendern({ antwortzusage: ANTWORTZUSAGE_VORSCHLAG[a.sprache] })}
                >
                  Empfohlenen Text einsetzen
                </button>
              )}
            </div>
          </Card>

          <Card className="space-y-3 p-4">
            <p className={TITEL}>Fragen auf der Dankeseite</p>
            <p className={HILFE}>
              Die Anfrage ist da, bevor der Gast diese Fragen sieht. Was er hier nachreicht, hängt an derselben Anfrage; auf der Dankeseite kosten
              die Felder keine Anfragen mehr. Eine Frage lässt sich im Schritt „Felder“ an eine Antwort knüpfen.
            </p>
            <ul className="grid gap-1 sm:grid-cols-2">
              {EDITOR_NACHFRAGE_FELDER.map((f) => (
                <li key={f.key}>
                  <label className="flex items-center gap-2">
                    <Checkbox checked={a.dankeseite.includes(f.key)} onChange={() => dankeseiteUmschalten(f.key)} />
                    <span className="font-body-sm text-body-sm text-on-surface dark:text-inverse-on-surface">{f.label}</span>
                  </label>
                </li>
              ))}
            </ul>
          </Card>
        </>
      )}

      <div className="flex justify-end">
        <Button type="button" onClick={onNext}>
          Weiter <Icon name="arrow_forward" className="text-[16px]" />
        </Button>
      </div>
    </div>
  );
}
